import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Heart, Sparkles } from 'lucide-react';
import SongMenu from '../SongMenu';

const Home = ({ playSong, songs, vibes, likedSongs, toggleLike, user }) => {
  const [madeForYou, setMadeForYou] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [newSongs, setNewSongs] = useState([]);
  const [loadingNewSongs, setLoadingNewSongs] = useState(true);
  const [trendingSongs, setTrendingSongs] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [loadingRecentlyPlayed, setLoadingRecentlyPlayed] = useState(true);
  const [randomizedVibes, setRandomizedVibes] = useState([]);
  
  // Randomize vibes on page load
  useEffect(() => {
    if (vibes.length > 0) {
      const shuffled = [...vibes].sort(() => Math.random() - 0.5);
      setRandomizedVibes(shuffled);
    }
  }, [vibes]);

  // Fetch Made For You, New Songs, and Trending in parallel
  useEffect(() => {
    const fetchData = async () => {
      // Fetch all in parallel for better performance
      const promises = [];
      
      // Made For You recommendations
      const recommendationsPromise = fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/recommendations/made-for-you`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      ).then(res => res.ok ? res.json() : [])
        .catch(() => []);
      
      // New Songs
      const newSongsPromise = fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/new`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      ).then(res => res.ok ? res.json() : [])
        .catch(() => []);
      
      // Trending Songs
      const trendingPromise = fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/trending`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      ).then(async res => {
        if (res.ok) {
          const data = await res.json();
          console.log('🔥 Trending API response:', data.length, 'songs');
          return data;
        } else {
          console.error('❌ Trending API failed:', res.status, res.statusText);
          const text = await res.text();
          console.error('Response body:', text);
          return [];
        }
      }).catch(err => {
        console.error('❌ Trending fetch error:', err);
        return [];
      });
      
      // Recently Played (actual listening history)
      const recentlyPlayedPromise = fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listening-history/recent?limit=6`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      ).then(res => res.ok ? res.json() : [])
        .catch(() => []);
      
      promises.push(recommendationsPromise, newSongsPromise, trendingPromise, recentlyPlayedPromise);
      
      const [recommendationsData, newSongsData, trendingData, recentlyPlayedData] = await Promise.all(promises);
      
      setMadeForYou(recommendationsData);
      setNewSongs(newSongsData);
      setTrendingSongs(trendingData);
      setRecentlyPlayed(recentlyPlayedData);
      setLoadingRecommendations(false);
      setLoadingNewSongs(false);
      setLoadingTrending(false);
      setLoadingRecentlyPlayed(false);
      
      console.log('✅ Loaded Made For You:', recommendationsData.length);
      console.log('✅ Loaded New Songs:', newSongsData.length);
      console.log('✅ Loaded Trending:', trendingData.length);
      console.log('✅ Loaded Recently Played:', recentlyPlayedData.length);
    };
    
    fetchData();
  }, [user]);

  const handlePlayVibe = (vibe) => {
    const vibeSongs = songs.filter(song => song.vibes.includes(vibe.id));
    if (vibeSongs.length > 0) {
      playSong(vibeSongs[0], vibeSongs);
    }
  };

  return (
    <div className="page-content" style={{ maxWidth: '1000px' }}>
      <div className="page-header">
        <div className="tagline">
          <span className="tagline-text" style={{ fontSize: '40px' }}>Music For Your Soul</span>
          <div className="tagline-description" style={{ fontSize: '16px' }}>
            The only free music platform devoted to mystical, metaphysical, and spiritual vibrations.
          </div>
        </div>
      </div>

      {/* Made For You Section - Moved below Vibes */}
      {/* Placeholder - will render after Vibes */}
      {false && (
        <section className="content-section">
          <div className="section-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={24} color="#FFD700" />
              <h2>Made For You</h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button 
                className="play-all-button"
                onClick={() => playSong(madeForYou[0], madeForYou)}
                style={{
                  background: '#9D4EDD',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                <Play size={16} fill="white" />
                Play All
              </button>
              <button 
                className="shuffle-button"
                onClick={() => {
                  const shuffled = [...madeForYou].sort(() => Math.random() - 0.5);
                  playSong(shuffled[0], shuffled);
                }}
                style={{
                  background: 'transparent',
                  color: '#9D4EDD',
                  border: '2px solid #9D4EDD',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/>
                </svg>
                Shuffle
              </button>
              <Link 
                to="/made-for-you"
                style={{
                  color: '#9D4EDD',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                View All ({madeForYou.length})
              </Link>
            </div>
          </div>
          <div className="songs-grid">
            {madeForYou.slice(0, 4).map(song => (
              <div key={song.id} className="song-card">
                <div className="song-card-image">
                  <img src={song.albumArt || song.album_art_url} alt={song.title} />
                  <SongMenu song={song} user={user} />
                  <button 
                    className="play-button"
                    onClick={() => playSong(song, madeForYou)}
                  >
                    <Play size={20} fill="white" />
                  </button>
                </div>
                <div className="song-card-content">
                  <h3 className="song-title">{song.title}</h3>
                  <p className="song-artist">{song.artist}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Trending Songs Section */}
      <section className="content-section">
          <div className="section-header">
            <div>
              <h2>Trending Now</h2>
              <p>What everyone's vibing to right now</p>
            </div>
            {trendingSongs.length > 4 && (
              <Link 
                to="/trending"
                style={{
                  color: '#9D4EDD',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                View All ({trendingSongs.length}) →
              </Link>
            )}
          </div>
          <div className="songs-grid" style={{ minHeight: '300px', gap: '16px' }}>
            {loadingTrending ? (
              // Loading skeleton - maintains space
              Array.from({ length: 4 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="song-card" style={{ opacity: 0.3 }}>
                  <div className="song-card-image" style={{ background: 'rgba(157, 78, 221, 0.1)' }}></div>
                  <div className="song-card-content">
                    <div style={{ height: '20px', background: 'rgba(157, 78, 221, 0.1)', borderRadius: '4px', marginBottom: '8px' }}></div>
                    <div style={{ height: '16px', background: 'rgba(157, 78, 221, 0.1)', borderRadius: '4px', width: '60%' }}></div>
                  </div>
                </div>
              ))
            ) : (
              trendingSongs.slice(0, 4).map(song => (
                <div key={song.id} className="song-card">
                <div className="song-card-image">
                  <img src={song.albumArt || song.album_art_url} alt={song.title} />
                  <SongMenu song={song} user={user} />
                  <button 
                    className="play-button"
                    onClick={() => playSong(song, trendingSongs)}
                  >
                    <Play size={20} fill="white" />
                  </button>
                </div>
                <div className="song-card-content">
                  <h3 className="song-title">{song.title}</h3>
                  <p className="song-artist">{song.artist}</p>
                </div>
              </div>
              ))
            )}
          </div>
      </section>

      {/* Just Dropped Section */}
      <section className="content-section">
          <div className="section-header">
            <div>
              <h2>Just Dropped</h2>
              <p>Recently added to the collection</p>
            </div>
            {newSongs.length > 4 && (
              <Link 
                to="/new-songs"
                style={{
                  color: '#9D4EDD',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                View All ({newSongs.length}) →
              </Link>
            )}
          </div>
          <div className="songs-grid" style={{ minHeight: '300px', gap: '16px' }}>
            {loadingNewSongs ? (
              // Loading skeleton - maintains space
              Array.from({ length: 4 }).map((_, i) => (
                <div key={`skeleton-new-${i}`} className="song-card" style={{ opacity: 0.3 }}>
                  <div className="song-card-image" style={{ background: 'rgba(157, 78, 221, 0.1)' }}></div>
                  <div className="song-card-content">
                    <div style={{ height: '20px', background: 'rgba(157, 78, 221, 0.1)', borderRadius: '4px', marginBottom: '8px' }}></div>
                    <div style={{ height: '16px', background: 'rgba(157, 78, 221, 0.1)', borderRadius: '4px', width: '60%' }}></div>
                  </div>
                </div>
              ))
            ) : (
              newSongs.slice(0, 4).map(song => (
                <div key={song.id} className="song-card">
                <div className="song-card-image">
                  <img src={song.albumArt || song.album_art_url} alt={song.title} />
                  <SongMenu song={song} user={user} />
                  <button 
                    className="play-button"
                    onClick={() => playSong(song, newSongs)}
                  >
                    <Play size={20} fill="white" />
                  </button>
                </div>
                <div className="song-card-content">
                  <h3 className="song-title">{song.title}</h3>
                  <p className="song-artist">{song.artist}</p>
                </div>
              </div>
              ))
            )}
          </div>
        </section>

      {/* Featured Vibes Section */}
      <section className="content-section">
        <div className="section-header">
          <div>
            <h2>Explore Vibes</h2>
            <p>Find your perfect energy match</p>
          </div>
          <Link to="/vibes" className="view-all-link">View All Vibes →</Link>
        </div>
        <div style={{
          display: 'flex',
          gap: '16px',
          overflowX: 'auto',
          overflowY: 'hidden',
          paddingBottom: '16px',
          scrollbarWidth: 'thin',
          scrollbarColor: '#9D4EDD #1a1a1a',
          WebkitOverflowScrolling: 'touch'
        }}>
          {randomizedVibes.map(vibe => (
            <Link 
              key={vibe.id} 
              to={`/vibe/${vibe.id}`}
              style={{
                minWidth: '280px',
                height: '200px',
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden',
                textDecoration: 'none',
                transition: 'transform 0.2s',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <div 
                style={{ 
                  width: '100%',
                  height: '100%',
                  backgroundColor: vibe.color,
                  backgroundImage: vibe.image_url ? `url(${vibe.image_url})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative'
                }}
              >
                {vibe.image_url && <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)'
                }} />}
                <div style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '20px',
                  right: '20px',
                  zIndex: 1
                }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#fff',
                    marginBottom: '8px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    {vibe.name}
                  </h3>
                  <p style={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: '8px',
                    lineHeight: '1.4',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                  }}>
                    {vibe.description}
                  </p>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: '600'
                  }}>
                    {vibe.songCount} songs
                  </div>
                </div>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    handlePlayVibe(vibe);
                  }}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'rgba(157, 78, 221, 0.95)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    zIndex: 2
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#9D4EDD';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(157, 78, 221, 0.95)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <Play size={24} fill="white" />
                </button>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Explore All Music Button */}
      <section className="content-section" style={{ 
        display: 'flex', 
        justifyContent: 'center',
        padding: '20px 0'
      }}>
        <Link 
          to="/all-music"
          style={{
            background: 'linear-gradient(135deg, #9D4EDD 0%, #7B2CBF 100%)',
            color: 'white',
            padding: '16px 48px',
            borderRadius: '30px',
            textDecoration: 'none',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 4px 20px rgba(157, 78, 221, 0.4)',
            transition: 'all 0.3s',
            display: 'inline-block'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 24px rgba(157, 78, 221, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(157, 78, 221, 0.4)';
          }}
        >
          Explore All Music
        </Link>
      </section>

      {/* Made For You Section - Below Vibes */}
      {user ? (
        !loadingRecommendations && madeForYou.length > 0 && (
          <section className="content-section">
            <div className="section-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={24} color="#FFD700" />
                <h2>Made For You</h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button 
                  className="play-all-button"
                  onClick={() => playSong(madeForYou[0], madeForYou)}
                  style={{
                    background: '#9D4EDD',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  <Play size={16} fill="white" />
                  Play All
                </button>
                <button 
                  className="shuffle-button"
                  onClick={() => {
                    const shuffled = [...madeForYou].sort(() => Math.random() - 0.5);
                    playSong(shuffled[0], shuffled);
                  }}
                  style={{
                    background: 'transparent',
                    color: '#9D4EDD',
                    border: '2px solid #9D4EDD',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/>
                  </svg>
                  Shuffle
                </button>
                <Link 
                  to="/made-for-you"
                  style={{
                    color: '#9D4EDD',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  View All ({madeForYou.length})
                </Link>
              </div>
            </div>
            <div className="songs-grid">
              {madeForYou.slice(0, 4).map(song => (
                <div key={song.id} className="song-card">
                  <div className="song-card-image">
                    <img src={song.albumArt || song.album_art_url} alt={song.title} />
                    <SongMenu song={song} user={user} />
                    <button 
                      className="play-button"
                      onClick={() => playSong(song, madeForYou)}
                    >
                      <Play size={20} fill="white" />
                    </button>
                  </div>
                  <div className="song-card-content">
                    <h3 className="song-title">{song.title}</h3>
                    <p className="song-artist">{song.artist}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )
      ) : (
        <section className="content-section">
          <div className="section-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={24} color="#FFD700" />
              <h2>Made For You</h2>
            </div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, rgba(157, 78, 221, 0.1) 0%, rgba(199, 125, 255, 0.1) 100%)',
            border: '2px solid rgba(157, 78, 221, 0.3)',
            borderRadius: '16px',
            padding: '48px 32px',
            textAlign: 'center'
          }}>
            <Sparkles size={48} color="#9D4EDD" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '24px', marginBottom: '12px', color: '#fff' }}>
              Get Personalized Recommendations
            </h3>
            <p style={{ fontSize: '16px', color: '#999', marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
              Sign in or create a free account to discover music tailored just for you based on your listening habits and preferences
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Link
                to="/login"
                style={{
                  background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                  color: 'white',
                  textDecoration: 'none',
                  padding: '12px 32px',
                  borderRadius: '24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'inline-block'
                }}
              >
                Sign In
              </Link>
              <Link
                to="/login?tab=signup"
                style={{
                  background: 'transparent',
                  color: '#9D4EDD',
                  border: '2px solid #9D4EDD',
                  textDecoration: 'none',
                  padding: '12px 32px',
                  borderRadius: '24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'inline-block'
                }}
              >
                Create Free Account
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Recently Played Section - Shows first 6 songs as placeholder */}
      {recentlyPlayed.length > 0 && (
        <section className="content-section">
          <div className="section-header">
            <h2>Recently Played</h2>
            <p>Jump back into your favorites</p>
          </div>
          <div className="songs-list">
            {recentlyPlayed.map((song, index) => (
            <div key={song.id} className="song-row">
              <div className="song-row-index">{index + 1}</div>
              <div className="song-row-image">
                <img src={song.albumArt} alt={song.title} />
                <button 
                  className="row-play-button"
                  onClick={() => playSong(song, songs)}
                >
                  <Play size={16} />
                </button>
              </div>
              <div className="song-row-info">
                <div className="song-row-title">{song.title}</div>
                <div className="song-row-artist">{song.artist}</div>
              </div>
              <div className="song-row-album">{song.album}</div>
              <div className="song-row-vibes">
                {song.vibes.slice(0, 2).map(vibeId => (
                  <span key={vibeId} className="vibe-tag">
                    {vibes.find(v => v.id === vibeId)?.name}
                  </span>
                ))}
              </div>
              <div className="song-row-duration">
                {Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')}
              </div>
              <button 
                className={`song-row-like ${likedSongs.has(song.id) ? 'liked' : ''}`}
                onClick={() => toggleLike(song.id)}
              >
                <Heart size={16} fill={likedSongs.has(song.id) ? '#9D4EDD' : 'none'} />
              </button>
            </div>
          ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;